import * as api from 'api';
import getDateForUrl from 'api/getDateForUrl';

import Select from '../Select/Select';
import Tools from '../Tools/Tools';

import LineGraph from './LineGraph';
import PieGraph from './PieGraph';

/**
 * Třída pro správu spojnicových a koláčových grafů.
 */
class Graph {
	constructor() {
		this.section = document.getElementsByClassName('Graph')[0];

		if (this.section && window.SSP.data.chart) {
			this.setGraphData();
			this.renderComponent();
		}
	}

	/**
	 * Nastaví typ stránky (Weby || Detail webu).
	 * Získá data pro prvotní načtení z windows.SPP.
	 */
	setGraphData() {
		this.setUrls();
		this.webActivityFilter = window.SSP.data.webActivityFilter;

		this.dataDimension = window.SSP.data.dimensionFilter;
		this.data = this.removeMetrics(window.SSP.data.chart);
	}

	/**
	 * CTR, Kliky zobrazuji pouze pokud mám vybrané weby/zony
	 * a filtr podle reklamního zdroje. V jiném případě data zahazuji.
	 * Z backendu záměrně chodí všechna data, i když nejsou třeba.
	 * @param {object} data
	 */
	removeMetrics(data) {
		if (!this.getSelectedWebsForUrl().length || this.dataDimension !== 'bySource') {
			if ('header' in data) {
				const metrics = data.header.map((metric) => metric.field);
				const clicksIndex = metrics.indexOf('clicks');
				const ctrIndex = metrics.indexOf('ctr');

				if (~clicksIndex && ~ctrIndex) {
					delete data.header[clicksIndex];
					delete data.header[ctrIndex];
				}
			}
		}

		return data;
	}

	/**
	 * Vyrenderuje graf z dat ve window. Podle nastavení uživatele
	 * nejdříve zobrazí spojnicový nebo koláčový graf.
	 */
	renderComponent() {
		// Renderujeme čárový graf.
		if (window.SSP.data.chartTypeSelector === 'lineChart') {
			this.section.classList.add('Graph--line');

			const ctx = this.getCanvasCtx(
				{
					height: 1,
					id: 'lineGraph',
					width: 6, // Width a height představuje poměr stran grafu.
				},
				'GraphLine-wrapper',
			);

			this.lineGraph = new LineGraph(ctx, this.data, this.section);
		} else {
			// Renderujeme koláčový graf.
			this.section.classList.add('Graph--pie');
			this.pieGraphs = {};

			this.data.header.forEach((metric) => {
				const ctx = this.getCanvasCtx(
					{
						id: 'pieGraph-' + metric.field,
					},
					'GraphPie-wrapper',
				);

				this.pieGraphs[metric.field] = new PieGraph(ctx, metric, this.data.series[metric.field], this.dataDimension);
			});
		}

		// Pokud se NErenderuje footer pro 'lineChart', tak tam nepatří select granularity.
		this.renderFooter(window.SSP.data.chartTypeSelector === 'lineChart');
		this.toggleNoDataMsg(this.data.series, window.SSP.data.chartTypeSelector);
		this.listenOnChange();
	}

	/**
	 * Dokumentace API na /api/docs/
	 */
	setUrls() {
		this.urls = {
			graphData: window.SSP.data.urls.chart + '?',
		};
	}

	/**
	 * Vytvoří canvas pro graf, vloží ho do stránky a vrátí 2d context,
	 * který vyžaduje constructor knihovny Chart.js.
	 * @param {object} canvasOptions id, classy apod. pro canvas element
	 * @param {string} wrapperClass class pro obalovací element canvasu
	 * @return {context}
	 */
	getCanvasCtx(canvasOptions, wrapperClass) {
		const canvas = Tools.createElement('canvas', canvasOptions);
		// Wrapper pro canvas, který umožňuje responzivní chování grafu.
		// Canvasu se nastavuje pouze poměr stran.
		const wrapper = Tools.createElement('div', { class: wrapperClass });

		wrapper.appendChild(canvas);

		// Graf musí být vždy vložen nad dolní ovládací tlačítka pokud existují.
		if (this.section.getElementsByClassName('Graph-buttons').length) {
			this.section.insertBefore(wrapper, this.section.getElementsByClassName('Graph-buttons')[0]);
		} else {
			this.section.appendChild(wrapper);
		}

		return canvas.getContext('2d');
	}

	/**
	 * Vytvoří tlačítko pro změnu typu grafu.
	 */
	getGraphTypeButton() {
		const buttonGraphType = Tools.createElement('button', {
			class: 'Graph-selectType',
			'data-next': window.SSP.data.chartTypeSelector === 'lineChart' ? 'pieChart' : 'lineChart',
			'data-selected': window.SSP.data.chartTypeSelector,
			innerText:
				window.SSP.data.chartTypeSelector === 'lineChart'
					? window.SSP.translations.chart.typeSelector[1][1]
					: window.SSP.translations.chart.typeSelector[0][1],
		});

		buttonGraphType.addEventListener('click', this.onChangeGraphType.bind(this));

		return buttonGraphType;
	}

	/**
	 * Upraví data tlačítka pro změnu grafu.
	 * @param {string} selected
	 */
	updateGraphTypeButton(selected) {
		this.buttonGraphType.innerText =
			selected === 'lineChart'
				? window.SSP.translations.chart.typeSelector[1][1]
				: window.SSP.translations.chart.typeSelector[0][1];

		this.buttonGraphType.setAttribute('data-selected', selected);

		this.buttonGraphType.setAttribute('data-next', selected === 'lineChart' ? 'pieChart' : 'lineChart');
	}

	/**
	 * Vyrenderuje patičku grafu se selectem granularity
	 * a přepínačem typu grafů.
	 * @param {boolean} granularityVisible Pokud false, tak bude select granularity schovaný.
	 */
	renderFooter(granularityVisible) {
		const wrapper = Tools.createElement('div', { class: 'Graph-buttons' });

		this.selectGranularity = new Select();

		this.selectGranularity.initSelectWithParams(
			window.SSP.translations.chart.granularitySelector,
			this.selectGranularityChange.bind(this),
			window.SSP.data.granularitySelector,
			'Select--bigger Graph-selectFilter' + (granularityVisible ? '' : ' is-hidden'),
		);

		this.buttonGraphType = this.getGraphTypeButton();

		Tools.appendChilds(wrapper, this.selectGranularity.getElement(), this.buttonGraphType);

		Tools.appendChilds(this.section, wrapper);
	}

	/**
	 * Reaguje na změnu granularity.
	 * @param {string} selected
	 */
	selectGranularityChange(selected) {
		this.selectGranularity.setSelectOption(selected);
		this.selectGranularity.toggleSelect();

		// stáhne data pro nové časové období
		api
			.GET(this.getUrl())
			.then(({ data }) => {
				this.data = this.removeMetrics(data);
				this.lineGraph.updateDataset(data);

				this.toggleNoDataMsg(data.series, this.buttonGraphType.getAttribute('data-selected'));
			})
			.catch((error) => {
				console.error(error);
			});
	}

	/**
	 * Pokud nejsou data, tak bude zobrazena hláška a schovány grafy.
	 * @param {object} data Data pro graf.
	 * @param {*} type Aktuálně zobrazený typ grafu.
	 */
	toggleNoDataMsg(data, type) {
		let visibile = true;

		// Vezme první metriku z dat a zjistí její velikost === 0 nejsou data
		if (Object.keys(data).length && !data[Object.keys(data)[0]].length) {
			// Vytvoří hlášku o neexistenci dat a vloží jí do DOMu. (pokud neexistuje)
			if (!this.section.getElementsByClassName('Graph-noData').length) {
				const elem = Tools.createElement('div', {
					class: 'Graph-noData',
					innerHTML: `
					<h3 class='Graph-noData-title'>${SSP.translations.chart.noData.header}</h3>
					<p class='Graph-noData-text'>${SSP.translations.chart.noData.text}</p>
				`,
				});

				this.section.insertBefore(elem, this.section.getElementsByClassName('Graph-buttons')[0]);
			}

			visibile = false;
		} else if (this.section.querySelector('.Graph-noData')) {
			// Data existují a bude odstraněna hláška.
			this.section.removeChild(this.section.getElementsByClassName('Graph-noData')[0]);
		}

		// Pokud se zobrazuje/schovává hláška, tak se zároveň musí upravit viditelnost grafů.
		if (type === 'lineChart') {
			this.toggleLineGraph(visibile, true);
		} else {
			this.togglePieGraph(visibile);
		}
	}

	/**
	 * Schová/zobrazí koláčový graf.
	 * @param {boolean} visible Zda bude element viditelný.
	 */
	togglePieGraph(visible) {
		Array.from(this.section.getElementsByClassName('GraphPie-wrapper')).forEach((pie) => {
			if (visible) {
				pie.classList.remove('is-invisible');
			} else {
				pie.classList.add('is-invisible');
			}
		});
	}

	/**
	 * Schová/zobrazí čárový graf.
	 * @param {boolean} visible Zda bude element viditelný.
	 * @param {boolen} keepControlls Neschová controlls.
	 */
	toggleLineGraph(visible, keepControlls) {
		const [controls] = this.section.getElementsByClassName('Graph-controls');
		const [wrapper] = this.section.getElementsByClassName('GraphLine-wrapper');

		if (controls && wrapper) {
			if (keepControlls) {
				controls.classList.remove('is-invisible');
			} else {
				controls.classList.add('is-invisible');
			}

			if (visible) {
				wrapper.classList.remove('is-invisible');
			} else {
				wrapper.classList.add('is-invisible');
			}
		}
	}

	/**
	 * Schová/zobrazí select s granularitou grafu.
	 * @param {boolean} visible Zda bude element viditelný.
	 */
	toggleGranularityButton(visible) {
		if (visible) {
			this.section.getElementsByClassName('Graph-selectFilter')[0].classList.remove('is-hidden');
		} else {
			this.section.getElementsByClassName('Graph-selectFilter')[0].classList.add('is-hidden');
		}
	}

	/**
	 * Reaguje na změnu typu grafu.
	 * @param {Event} e
	 */
	onChangeGraphType(e) {
		if (e.target.getAttribute('data-pending')) {
			return;
		} else {
			e.target.setAttribute('data-pending', true);
		}

		const selected = e.target.getAttribute('data-next');

		if (selected === 'lineChart') {
			api
				.GET(this.getUrl(null, selected))
				.then(({ data }) => {
					this.updateGraphTypeButton(selected);
					// Schovám koláčový graf a zobrazím tlačítko granularity.
					this.togglePieGraph(false);
					this.toggleGranularityButton(true);
					Tools.replaceClass(this.section, 'Graph--line', 'Graph--pie');

					this.data = this.removeMetrics(data);

					// Pokud už graf existuje, tak pouze obnovím data (pro případ, že bylo změněno datum, filtr apod.)
					if (this.lineGraph) {
						this.lineGraph.updateDataset(data);
					} else {
						const ctx = this.getCanvasCtx(
							{
								height: 1,
								id: 'lineGraph',
								width: 6, // width a height představuje poměr stran grafu
							},
							'GraphLine-wrapper',
						);

						this.lineGraph = new LineGraph(ctx, this.data, this.section);
					}

					this.toggleNoDataMsg(data.series, selected);
					e.target.removeAttribute('data-pending');
				})
				.catch((error) => console.error(error));
		} else {
			api
				.GET(this.getUrl(null, selected))
				.then(({ data }) => {
					this.updateGraphTypeButton(selected);
					// Schovám čárový graf a select pro granularitu
					this.toggleLineGraph(false);
					this.toggleGranularityButton(false);
					Tools.replaceClass(this.section, 'Graph--pie', 'Graph--line');

					this.data = data;

					// Koláčový graf už existuje, ale je schovaný.
					if (this.pieGraphs) {
						// Updatuju existují grafy.
						data.header.forEach((metric) => {
							this.pieGraphs[metric.field].updateDataset(data.series[metric.field], metric, this.dataDimension);
						});
					} else {
						// Vytvořím nové koláčové grafy.
						this.pieGraphs = {};

						this.data.header.forEach((metric) => {
							const ctx = this.getCanvasCtx(
								{
									id: 'pieGraph-' + metric.field,
								},
								'GraphPie-wrapper',
							);

							this.pieGraphs[metric.field] = new PieGraph(ctx, metric, this.data.series[metric.field]);
						});
					}

					this.toggleNoDataMsg(data.series, selected);
					e.target.removeAttribute('data-pending');
				})
				.catch((error) => console.error(error));
		}
	}

	/**
	 * Naformátuje string s granularitou pro API cally.
	 * @return {string}
	 */
	getGranularityForUrl() {
		return '&granularity=' + this.selectGranularity.getSelectedOption();
	}

	/**
	 * Naformátuje string s typem grafu pro API cally.
	 * @param {string} selected
	 * @return {string}
	 */
	getGraphTypeForUrl(selected) {
		return '&chartType=' + (selected ? selected : this.buttonGraphType.getAttribute('data-selected'));
	}

	/**
	 * Naformátuje string s filterm activity webu pro API cally.
	 * @return {string}
	 */
	getWebActivityForUrl() {
		return '&webActivity=' + this.webActivityFilter;
	}

	/**
	 * Naformátuje string s vybranými weby pro URL.
	 * @return {string}
	 */
	getSelectedWebsForUrl() {
		if (this.selectedWebs) {
			return this.selectedWebs;
		} else {
			return '';
		}
	}

	/**
	 * Vytvoří URL pro API cally.
	 * @param {object} date
	 * @param {string} selected graphType
	 * @return {string}
	 */
	getUrl(date, selected) {
		let url =
			this.urls.graphData +
			getDateForUrl(date ? date : false) +
			this.getGranularityForUrl() +
			this.getGraphTypeForUrl(selected) +
			this.getWebActivityForUrl() +
			this.getSelectedWebsForUrl();

		if (selected === 'pieChart' || this.buttonGraphType.getAttribute('data-selected') === 'pieChart') {
			url += '&dimension=' + this.dataDimension;
		}

		return url;
	}

	/**
	 * Naslouchá na změny na stránce, které překreslí graf.
	 */
	listenOnChange() {
		this.listenOnDateChange();
		this.listenOnActivityChange();
		this.listenOnWebsSelection();
		this.listenOnDimensionChange();
	}

	/**
	 * Obnoví buď koláčový nebo čárový graf.
	 */
	updateGraphs() {
		api
			.GET(this.getUrl())
			.then(({ data }) => {
				this.toggleNoDataMsg(data.series, this.buttonGraphType.getAttribute('data-selected'));

				if (this.buttonGraphType.getAttribute('data-selected') === 'lineChart') {
					this.lineGraph.updateDataset(this.removeMetrics(data));
				} else {
					data.header.forEach((metric) => {
						this.pieGraphs[metric.field].updateDataset(data.series[metric.field], metric, this.dataDimension);
					});
				}
			})
			.catch((error) => console.error(error));
	}

	/**
	 * Pokud si uživatel nechá zobrazit pouze vybrané weby,
	 * tak bude graf překreslen
	 */
	listenOnWebsSelection() {
		window.addEventListener('only-selected-rows', (e) => {
			this.selectedWebs = e.detail.selected;
			this.updateGraphs();
		});
	}

	/**
	 * Pokud uživatel pod tabulkou změní filtr aktivity, tak bude
	 * graf překreslen.
	 */
	listenOnActivityChange() {
		window.addEventListener('rows-activity-changed', (e) => {
			this.webActivityFilter = e.detail.selected;
			// Budou načteny data pro všechny weby a ne pouze pro výběr.
			this.selectedWebs = null;
			this.updateGraphs();
		});
	}

	/**
	 * Pokud uživatel změní datum pomocí kalendáře,
	 * tak budou načtena nová data. Přerenderuje se
	 * graf.
	 */
	listenOnDateChange() {
		window.addEventListener('calendar-changed', () => {
			this.updateGraphs();
		});
	}

	/**
	 * Pokud uživatel změní dimenzi (podle zdroje/zařízení)
	 * a je vybrán koláčový graf, tak bude updatován.
	 */
	listenOnDimensionChange() {
		window.addEventListener('data-dimension-changed', (e) => {
			this.dataDimension = e.detail.selected;
			this.updateGraphs();
		});
	}
}

export default Graph;
